/**
 * FAQ Accordion — Frontend interaction (view.js)
 *
 * Vanilla JS, no external dependencies.
 * Enqueued automatically by WordPress when the block is rendered
 * (declared as "viewScript" in block.json).
 *
 * Accessibility:
 *  - Manages aria-expanded on the trigger button.
 *  - Keyboard: Enter / Space are handled natively by <button>.
 *  - The server-rendered [hidden] attribute is swapped for .is-collapsed
 *    at init time so CSS grid-template-rows transitions can animate.
 *
 * Behaviour:
 *  - Exclusive accordion: only one panel open at a time.
 *
 * @package waiis
 */

'use strict';

/**
 * Close a single accordion item.
 *
 * @param {HTMLButtonElement} trigger
 */
function closeItem( trigger ) {
	var contentId = trigger.getAttribute( 'aria-controls' );
	var panel     = document.getElementById( contentId );

	trigger.setAttribute( 'aria-expanded', 'false' );

	if ( panel ) {
		panel.classList.add( 'is-collapsed' );
	}

	var icon = trigger.querySelector( '.waiis-faq-accordion__icon' );
	if ( icon ) {
		icon.classList.remove( 'is-open' );
	}
}

/**
 * Open a single accordion item.
 *
 * @param {HTMLButtonElement} trigger
 */
function openItem( trigger ) {
	var contentId = trigger.getAttribute( 'aria-controls' );
	var panel     = document.getElementById( contentId );

	trigger.setAttribute( 'aria-expanded', 'true' );

	if ( panel ) {
		panel.classList.remove( 'is-collapsed' );
	}

	var icon = trigger.querySelector( '.waiis-faq-accordion__icon' );
	if ( icon ) {
		icon.classList.add( 'is-open' );
	}
}

/**
 * Handle a trigger click: close all siblings, then toggle the clicked item.
 *
 * @param {HTMLButtonElement}   trigger     - The clicked trigger.
 * @param {HTMLButtonElement[]} allTriggers - All triggers in this accordion instance.
 */
function handleTriggerClick( trigger, allTriggers ) {
	var isExpanded = trigger.getAttribute( 'aria-expanded' ) === 'true';

	// Always close every item first.
	allTriggers.forEach( function ( t ) {
		closeItem( t );
	} );

	// If the clicked item was previously closed, open it.
	if ( ! isExpanded ) {
		openItem( trigger );
	}
}

/**
 * Initialise one FAQ Accordion block instance.
 *
 * @param {HTMLElement} container - The .waiis-faq-accordion wrapper element.
 */
function initAccordion( container ) {
	var triggers = Array.from( container.querySelectorAll( '[data-accordion-trigger]' ) );

	// Replace server-rendered [hidden] with .is-collapsed so CSS transitions work.
	triggers.forEach( function ( trigger ) {
		var contentId = trigger.getAttribute( 'aria-controls' );
		var panel     = document.getElementById( contentId );

		if ( panel && panel.hasAttribute( 'hidden' ) ) {
			panel.removeAttribute( 'hidden' );
			panel.classList.add( 'is-collapsed' );
		}
	} );

	triggers.forEach( function ( trigger ) {
		trigger.addEventListener( 'click', function () {
			handleTriggerClick( trigger, triggers );
		} );
	} );
}

/**
 * Boot — finds every block instance on the page and initialises it.
 * Runs immediately if the DOM is ready, or on DOMContentLoaded otherwise.
 */
function boot() {
	document
		.querySelectorAll( '.waiis-faq-accordion[data-component="accordion"]' )
		.forEach( initAccordion );
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', boot );
} else {
	boot();
}
